import { useIntl } from "react-intl";
import type { AutocompletePlace } from "src/api/v1.6/autocomplete";
import { useId, useRef, useState, Fragment } from "react";
import X from "src/design-system/icons/X.svg?react";
import PinSolid from "src/design-system/icons/PinSolid.svg?react";
import { AddDestinationTimelineButton } from "src/components/TripPlanner/AddDestinationTimelineButton/AddDestinationTimelineButton";
import { getSurroundingPlaces } from "src/utils/getSurroundingPlaces";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { FocusContext, type FocusedElement } from "src/FocusContext";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { Skeleton } from "src/components/Skeleton/Skeleton";
import {
  useSearchInput,
  useSearchKeyboard,
} from "src/utils/hooks/useSearchInput";
import { SearchInput } from "src/components/SearchInput/SearchInput";
import { tw } from "src/design-system/tw";
import { Button } from "react-aria-components";
import { TripPlannerAutocomplete } from "../TripPlannerAutocomplete/TripPlannerAutocomplete";
import { DottedLine } from "../DottedLine/DottedLine";
import messages from "./PlaceInputList.messages";

function PlaceInputSkeleton() {
  return <Skeleton width="100%" height="48px" />;
}

type PlaceInputListProps = {
  places: string[];
  loadingIndex?: number;
  handleRemoveDestination: (index: number) => void;
  handleSelectPlace: (
    autocompletePlace: AutocompletePlace,
    index: number,
  ) => void;
  isInline?: boolean;
};

export function PlaceInputList({
  places,
  handleSelectPlace,
  handleRemoveDestination,
  isInline,
  loadingIndex,
}: PlaceInputListProps) {
  const intl = useIntl();
  const listRef = useRef<HTMLUListElement>(null);
  const InputComponent = isInline ? Input : ButtonInput;
  const hasAtleast2Legs = places.filter((place) => !!place).length > 1;
  const { isDesktop } = useScreenMinWidth();

  function selectAndProceed(place: AutocompletePlace, index: number) {
    handleSelectPlace(place, index);
    const nextInput = listRef.current?.querySelectorAll("input")[index + 1];
    if (nextInput && !nextInput.value) {
      nextInput.focus();
    }
  }

  return (
    <div className="flex flex-col gap-0.5">
      <ul className="flex flex-col gap-0.5" ref={listRef}>
        {places.map((place, index) => {
          const placeHolder =
            index === 0
              ? intl.formatMessage(messages.startingFrom)
              : intl.formatMessage(messages.goingTo);
          return (
            // We want the key to stable be based on order,
            // so the inputs do not remount each time the place changed
            <Fragment key={`${place}-${index}`}>
              <InputComponent
                as="li"
                {...{
                  index,
                  place,
                  places,
                  placeHolder,
                  handleSelectPlace: selectAndProceed,
                  handleRemoveDestination,
                }}
              />
              {places.length - 1 !== index && !isDesktop ? (
                <div className="relative z-1 mx-4 flex w-2.5 items-center justify-center">
                  <span className="absolute h-11 border-l border-l-icon-secondary"></span>
                </div>
              ) : null}
            </Fragment>
          );
        })}
        {loadingIndex === places.length && <PlaceInputSkeleton />}
      </ul>
      {!isDesktop && <DottedLine />}
      <AddDestinationButton
        handleAddDestination={selectAndProceed}
        isDisabled={!hasAtleast2Legs}
        places={places}
      />
    </div>
  );
}

type InputProps = {
  index: number;
  place: string;
  places: string[];
  placeHolder: string;
  handleSelectPlace: (place: AutocompletePlace, index: number) => void;
  handleRemoveDestination: (index: number) => void;
};

function ButtonInput({
  index,
  handleSelectPlace,
  handleRemoveDestination,
  place,
  places,
  placeHolder,
}: Readonly<InputProps>) {
  const [isAutocompleteActive, setIsAutocompleteActive] = useState(false);
  const intl = useIntl();

  return (
    <>
      {isAutocompleteActive && (
        <TripPlannerAutocomplete
          onSelectOption={(place) => {
            handleSelectPlace(place, index);
            setIsAutocompleteActive(false);
          }}
          handleClose={() => setIsAutocompleteActive(false)}
          places={getSurroundingPlaces(index, places)}
        />
      )}
      <li
        className={tw(
          "relative flex items-center bg-surface-active",
          "first:rounded-t-md last-of-type:rounded-b-md",
        )}
      >
        <InputIcon type={getIconType(index, places)} isEmpty={!place} />
        <Button
          className={tw(
            "flex flex-1 items-center justify-start overflow-hidden px-0 py-4 pl-10",
            "text-base leading-none whitespace-nowrap text-text-primary-active",
            !place && "text-text-primary",
          )}
          onClick={() => setIsAutocompleteActive(true)}
        >
          <span className="flex-1 overflow-hidden text-left leading-tight text-ellipsis">
            {!!place ? place : placeHolder}
          </span>
        </Button>

        {handleRemoveDestination && (
          <Button
            className="absolute top-1/2 right-2 flex -translate-y-1/2 p-4"
            onClick={() => handleRemoveDestination(index)}
            aria-label={intl.formatMessage(messages.removePlace)}
          >
            <X className="h-6 w-6 text-icon-primary" />
          </Button>
        )}
      </li>
    </>
  );
}

function Input({
  place,
  handleSelectPlace,
  index,
  places,
  placeHolder,
  handleRemoveDestination,
  as,
}: InputProps & { as?: "li" }) {
  const id = useId();
  const intl = useIntl();
  const keyboard = useSearchKeyboard();
  const search = useSearchInput(
    {
      id,
      initialQuery: place,
      onSelect: onSelect,
    },
    keyboard,
  );

  function onSelect(place: AutocompletePlace) {
    handleSelectPlace(place, index);
  }

  function onFocusChanged(focusedElement: FocusedElement) {
    const numResults = search.displayResults.length;
    keyboard.setFocusedElement(focusedElement, numResults);
  }

  return (
    <FocusContext
      value={{
        focusedElement: keyboard.focusedElement,
        onFocusChanged,
      }}
    >
      <SearchInput
        as={as}
        className="box-border min-h-12 rounded-md border border-border bg-surface pl-2.5 hover:bg-surface-hover"
        id={id}
        search={search}
        keyboard={keyboard}
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={
          (index === 0 && !search.displayValue) ||
          (!search.displayValue && !!places[0])
        }
        placeholder={placeHolder}
        LeftElement={
          <InputIcon
            type={getIconType(index, places)}
            isEmpty={!search.displayValue}
          />
        }
        RightElement={
          places.length > 2 ? (
            <Button
              className="group absolute top-1/2 right-2 flex -translate-y-1/2 p-4"
              onClick={() => handleRemoveDestination(index)}
              aria-label={intl.formatMessage(messages.removePlace)}
            >
              <X className="h-6 w-6 text-icon-primary group-focus-visible:outline-2" />
            </Button>
          ) : undefined
        }
      />
    </FocusContext>
  );
}

function getIconType(index: number, places: string[]) {
  if (index === 0 && places.length <= 2) {
    return "origin";
  }
  if (index === 1 && places.length <= 2) {
    return "destination";
  }
  return "place";
}

function InputIcon({
  type,
  isEmpty,
}: {
  type: "origin" | "destination" | "place";
  isEmpty?: boolean;
}) {
  switch (type) {
    case "origin":
      return (
        <div
          className={tw(
            "absolute top-1/2 left-3 z-10 ml-px -translate-y-1/2",
            "box-content h-2 w-2 rounded-full border-5 border-icon-primary",
            "md:static md:ml-0.5 md:-translate-y-0",
            isEmpty && "border-border-brand",
          )}
        ></div>
      );
    case "destination":
      return (
        <PinSolid
          className={tw(
            "absolute top-1/2 left-2 z-1 ml-px -translate-y-1/2 text-icon-primary",
            "md:static md:-ml-px md:-translate-y-0",
            isEmpty && "text-icon-brand",
          )}
        />
      );
    case "place":
      return (
        <div
          className={tw(
            "absolute top-1/2 -left-px mx-4 h-3 w-3 flex-shrink-0 -translate-y-1/2",
            "rounded-full bg-icon-primary md:static md:mr-1 md:ml-1 md:translate-y-0",
            "lg:mr-2 lg:h-2.5 lg:w-2.5",
          )}
        ></div>
      );
  }
}

function AddDestinationButton({
  handleAddDestination,
  isDisabled,
  places,
}: {
  handleAddDestination: (place: AutocompletePlace, index: number) => void;
  isDisabled: boolean;
  places: string[];
}) {
  const intl = useIntl();
  const [isInputVisible, setIsInputVisible] = useState(false);
  const { isSmScreen } = useScreenMinWidth();
  const isInline = isSmScreen && isInputVisible;
  const isFullScreen = !isSmScreen && isInputVisible;

  return (
    <>
      {isFullScreen && (
        <TripPlannerAutocomplete
          handleClose={() => setIsInputVisible(false)}
          onSelectOption={(place) => {
            handleAddDestination(place, places.length);
            setIsInputVisible(false);
          }}
          places={[places[places.length - 1]]}
        />
      )}

      {isInline && (
        <Input
          place=""
          placeHolder={intl.formatMessage(messages.goingTo)}
          index={places.length}
          places={places}
          handleSelectPlace={(place, index) => {
            handleAddDestination(place, index);
            setIsInputVisible(false);
          }}
          handleRemoveDestination={() => {
            setIsInputVisible(false);
          }}
        />
      )}

      <AddDestinationTimelineButton
        isDisabled={isDisabled || isInputVisible}
        onClick={() => {
          sendAnalyticsInteractionEvent({
            category: "CreateTrip",
            action: "Click:AddDestination",
          });
          setIsInputVisible(true);
        }}
      />
    </>
  );
}
